import { useState, useCallback } from "react";
import { Area } from "../models/Area";
import { saveAreaRatingsDiff, submitAssessment } from "../services/ratingsService";
import { computeAreaProgress } from "../utils/areaProgress";

interface UseRatingsSyncProps {
  isLoggedIn: boolean;
  areas: Area[];
  ratings: Record<string, number>;
  draftEntry: any;
  getAccessToken: () => Promise<string>;
  onSubmitSuccess: () => void;
}

export const useRatingsSync = ({
  isLoggedIn,
  areas,
  ratings,
  draftEntry,
  getAccessToken,
  onSubmitSuccess
}: UseRatingsSyncProps) => {
  const [syncState, setSyncState] = useState<"idle" | "running" | "done" | "error">("idle");
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const buildFullDiff = useCallback(() => {
    const changes: {
      areaId: number;
      subAreaId: number;
      subAreaDetailId: number;
      rating: number;
    }[] = [];
    
    areas.forEach(area => {
      (area.subArea ?? []).forEach(sa => {
        (sa.subAreaDetails ?? []).forEach(d => {
          const key = `${sa.subAreaId}-${d.subAreaDetailId}`;
          if (typeof ratings[key] === "number" && ratings[key] > -1) {
            changes.push({
              areaId: area.areaId,
              subAreaId: sa.subAreaId,
              subAreaDetailId: d.subAreaDetailId,
              rating: ratings[key]
            });
          }
        });
      });
    });
    return changes;
  }, [areas, ratings]);

  const handleFullSync = useCallback(async () => {
    if (!isLoggedIn || syncState === "running") return;
    setSyncState("running");
    
    try {
      const changes = buildFullDiff().map(c => ({
        areaId: c.areaId,
        subAreaId: c.subAreaId,
        subAreaDetailId: c.subAreaDetailId,
        rating: c.rating
      }));
      
      const body = {
        userId: 0,
        requestPeriodId: draftEntry?.requestPeriodId ?? 0,
        areaId: -1,
        changes
      };
      const res = await saveAreaRatingsDiff(body, getAccessToken);
      if (!res.ok) {
        setSyncState("error");
        setTimeout(() => setSyncState("idle"), 3000);
        return;
      }
      
      setSyncState("done");
      setTimeout(() => setSyncState("idle"), 2500);
    } catch (e) {
      console.error("Full sync failed", e);
      setSyncState("error");
      setTimeout(() => setSyncState("idle"), 3000);
    }
  }, [isLoggedIn, syncState, buildFullDiff, draftEntry, getAccessToken]);

  const handleSubmit = useCallback(async () => {
    if (!isLoggedIn || submitState === "submitting") return;
    
    const allAreasComplete =
      areas.length > 0 &&
      areas.every(a => computeAreaProgress(a, ratings).complete);

    if (!allAreasComplete) return;

    setSubmitState("submitting");
    try {
      const periodId = draftEntry?.requestPeriodId ?? 1;
      const res = await submitAssessment(
        0,
        periodId,
        getAccessToken
      );
      
      if (!res.ok) {
        setSubmitState("error");
        setTimeout(() => setSubmitState("idle"), 3000);
        return;
      }
      
      setSubmitState("success");
      onSubmitSuccess();
      setTimeout(() => setSubmitState("idle"), 4000);
    } catch (e) {
      console.error("Submit failed", e);
      setSubmitState("error");
      setTimeout(() => setSubmitState("idle"), 3000);
    }
  }, [areas, ratings, getAccessToken, submitState, draftEntry, isLoggedIn, onSubmitSuccess]);

  return {
    syncState,
    submitState,
    handleFullSync,
    handleSubmit
  };
};
